import { useEffect, useState } from 'react';
import {
  useGetAlarmHistoryPaginatedQuery,
  useGetPendingAlarmsPaginatedQuery,
} from 'services/alarms/alarms.api';
import { connectToLiveAlarms } from 'services/alarms/alarms.service';
import useDebounce from './useDebounce';

export const LIMIT_OPTIONS = [25, 50, 100];

/**
 * Use alarm data fetched from fusion api.
 * @param {boolean} [pending] True = Use pending alarms, False = Use alarm history
 * @returns {{totalCount: number, alarms: {ackowledged: *[], activity: string, ca_identifier: string, cw_identifier: string, device: string, expire_time: string, ga_identifier: string, headline: string, id: number, info: Object, message: string, organization_id: number, silenced: boolean, silenced_by: (string | number | null), source: string, state: boolean}[], fetchAlarms: (limit: number, page: number, search: string), setLimit: (limit: number), setPage: (page: number), setSearch: (search: string), LIMIT_OPTIONS: number[]}}
 */
export default function useAlarms(pending = false) {
  const [limit, setLimit] = useState(LIMIT_OPTIONS[0]);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const searchDebounce = useDebounce(search, 500);
  const { data: pendingAlarms, refetch: refetchPendingAlarms, isLoading: isPendingLoading, isFetching: isPendingFetching } = useGetPendingAlarmsPaginatedQuery(
    { limit, offset: page * limit, search: searchDebounce.toLowerCase() },
    { skip: !pending }, // Skip this query if we're showing alarm history instead of pending alarms
  );
  const { data: alarmHistory, refetch: refetchAlarmHistory, isLoading: isHistoryLoading, isFetching: isHistoryFetching } = useGetAlarmHistoryPaginatedQuery(
    {
      limit: limit,
      offset: page * limit,
      search: searchDebounce.toLowerCase(),
    },
    { skip: pending }, // Skip this query if we're showing pending alarms
  );
  const alarms = pending ? pendingAlarms?.data || [] : alarmHistory?.data || [];
  const totalCount = pending ? pendingAlarms?.totalCount || 0 : alarmHistory?.totalCount || 0;
  const isLoading = pending ? (isPendingLoading || isPendingFetching) : (isHistoryLoading || isHistoryFetching);

  useEffect(() => {
    const eventSource = connectToLiveAlarms();

    eventSource.onmessage = async (msg) => {
      if (pending) refetchPendingAlarms();
      else refetchAlarmHistory();
    };

    eventSource.onerror = (err) => {
      console.error('Error from server in useAlarms:', err);
      eventSource.close();
    };

    eventSource.onclose = (e) => {
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchDebounce]);

  return {
    totalCount,
    alarms,
    limit,
    setLimit: (_limit) => {
      setLimit(_limit);
      setPage(0);
    },
    page,
    setPage,
    search,
    setSearch,
    LIMIT_OPTIONS,
    isLoading,
  };
}
